import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Drone,
  DroneOwnership,
  OwnershipFilter,
  PositionUpdate,
} from '../../types/drone';
import { FleetLayout } from '../../types/user';

export type { OwnershipFilter };

interface DronesState {
  drones: Record<string, Drone>;
  selectedDroneId: string | null;
  loading: boolean;
  error: string | null;
  /**
   * Bumped to ask useDroneData to refetch the fleet (#480).
   *
   * The fleet used to be fetched exactly once, on mount, so a drone enrolled
   * while the UI was open never appeared — and live telemetry for it was
   * dropped by updateDronePosition, which only updates drones already present.
   * The operator saw nothing and reasonably concluded enrolment had failed.
   */
  refreshNonce: number;
  /**
   * Which drones the fleet view shows (#385).
   *
   * A *first* visit opens on `all`, not `mine`. Owner scoping is what makes
   * "my drones" meaningful, but an unclaimed aircraft — one registered through
   * the factory flow, with no operator to own it — can be airborne, and
   * defaulting to a view that hides it would mean nobody is watching it.
   *
   * After that the operator's own choice is the default: it is saved to their
   * preferences and hydrated on load, so "show only mine" stays selected
   * across reloads and devices.
   */
  ownershipFilter: OwnershipFilter;
  /**
   * Whether the operator has picked a view in this session.
   *
   * Guards the hydration race: preferences arrive a few hundred milliseconds
   * after the first paint, and without this a filter chosen in that window
   * would be snapped back to the stored value the operator was in the middle of
   * changing.
   */
  ownershipChosen: boolean;
  /**
   * How the Fleet page lays drones out (#412). Kept here beside the ownership
   * filter, and saved and hydrated the same way, for the same reason: a choice
   * made before the stored preferences arrive must not be overwritten by them.
   */
  fleetLayout: FleetLayout;
  fleetLayoutChosen: boolean;
}

/**
 * Who owns this drone, as far as this client can tell.
 *
 * A drone that never came through `getAllDrones` — built in a test, or arriving
 * as a WebSocket telemetry frame for an aircraft the list has not caught up
 * with — has no bucket, and `mine` is the reading that renders it as ordinary
 * rather than flagging it as somebody else's.
 */
export const ownershipOf = (drone: Drone): DroneOwnership =>
  drone.ownership ?? 'mine';

/**
 * Whether a drone belongs in the given view.
 *
 * One definition, used by the map, the counts and the selection-clearing rule
 * above — three places that must agree about what "mine" means, and would
 * otherwise each answer it separately.
 *
 * They did all agree, on a definition that was wrong (#575): `mine` meant
 * "has an owner", which is the same set as "owned by me" only for a caller
 * whose response cannot contain anyone else's drones. An admin's can. The
 * definition now comes from the server, which is the only party that knows both
 * the owner and the viewer.
 */
export const matchesOwnershipFilter = (
  drone: Drone,
  filter: OwnershipFilter
): boolean => filter === 'all' || ownershipOf(drone) === filter;

const initialState: DronesState = {
  drones: {},
  selectedDroneId: null,
  loading: false,
  error: null,
  refreshNonce: 0,
  ownershipFilter: 'all',
  ownershipChosen: false,
  fleetLayout: 'grid',
  fleetLayoutChosen: false,
};

const dronesSlice = createSlice({
  name: 'drones',
  initialState,
  reducers: {
    setDrones: (state, action: PayloadAction<Drone[]>) => {
      const previous = state.drones;
      state.drones = {};
      action.payload.forEach((drone) => {
        // Live telemetry outranks the fleet list for the fields the list does
        // not carry (#508). GET /api/v1/drones answers with last_known_position
        // and nothing else about motion, so a refetch — after enrolling a drone,
        // say — would otherwise wipe the position, heading and speed that the
        // WebSocket had already filled in, and the markers would vanish from a
        // map the operator is watching.
        const live = previous[drone.id];
        state.drones[drone.id] =
          live?.position && !drone.position
            ? {
                ...drone,
                position: live.position,
                heading: live.heading,
                speed: live.speed,
                battery: live.battery,
                lastUpdateTime: live.lastUpdateTime,
              }
            : drone;
      });
      state.loading = false;
      state.error = null;
    },
    /**
     * Ask for a fresh fleet fetch. Dispatched after enrolling a drone, and when
     * telemetry arrives for a drone this client has never heard of.
     */
    fleetRefreshRequested: (state) => {
      state.refreshNonce += 1;
    },
    updateDrone: (state, action: PayloadAction<Drone>) => {
      state.drones[action.payload.id] = action.payload;
    },
    /** The server accepted a new name (#406), already trimmed by it. */
    droneRenamed: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const drone = state.drones[action.payload.id];
      if (drone) {
        drone.name = action.payload.name;
      }
    },
    setFleetLayout: (state, action: PayloadAction<FleetLayout>) => {
      state.fleetLayout = action.payload;
      state.fleetLayoutChosen = true;
    },
    /** The stored layout arrived; declines to overwrite one already chosen. */
    fleetLayoutHydrated: (state, action: PayloadAction<FleetLayout>) => {
      if (!state.fleetLayoutChosen) {
        state.fleetLayout = action.payload;
      }
    },
    // A live telemetry frame (#710). Heading and speed are written even when
    // absent (#711): a frame without a heading is the aircraft saying it does
    // not know, and keeping the previous one would draw a nose nobody measured.
    // Status is left as it was, because the frame does not carry it.
    updateDronePosition: (state, action: PayloadAction<PositionUpdate>) => {
      const { droneId, position, heading, speed, battery, lastUpdateTime, status } =
        action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId] = {
          ...state.drones[droneId],
          position,
          heading,
          speed,
          battery,
          lastUpdateTime,
          ...(status === undefined ? {} : { status }),
        };
      }
    },
    /**
     * The API marked a drone that stopped reporting (#787): offline, or link
     * lost if it was in the air. Its position stays where it was last heard;
     * that is the point of keeping it on the map.
     */
    droneStatusChanged: (
      state,
      action: PayloadAction<{ droneId: string; status: Drone['status']; lastUpdateTime: string }>
    ) => {
      const { droneId, status, lastUpdateTime } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId] = { ...state.drones[droneId], status, lastUpdateTime };
      }
    },
    selectDrone: (state, action: PayloadAction<string | null>) => {
      state.selectedDroneId = action.payload;
    },
    setOwnershipFilter: (state, action: PayloadAction<OwnershipFilter>) => {
      state.ownershipFilter = action.payload;
      state.ownershipChosen = true;
      // A drone hidden by the new filter must not stay selected, or its details
      // panel goes on describing an aircraft the map no longer shows.
      const selected = state.selectedDroneId
        ? state.drones[state.selectedDroneId]
        : undefined;
      if (selected && !matchesOwnershipFilter(selected, action.payload)) {
        state.selectedDroneId = null;
      }
    },
    /**
     * The stored view arrived from the server. Same effect as choosing it, kept
     * separate so the intent is legible where it is dispatched — and so it can
     * decline to overwrite a choice the operator has already made.
     */
    ownershipHydrated: (state, action: PayloadAction<OwnershipFilter>) => {
      if (state.ownershipChosen) {
        return;
      }
      state.ownershipFilter = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const {
  setDrones,
  fleetRefreshRequested,
  updateDrone,
  droneRenamed,
  setFleetLayout,
  fleetLayoutHydrated,
  updateDronePosition,
  droneStatusChanged,
  selectDrone,
  setOwnershipFilter,
  ownershipHydrated,
  setLoading,
  setError,
} = dronesSlice.actions;

export default dronesSlice.reducer;
